import {
  createBatchLoader,
  useRead,
} from '@spotify-internal/creator-data-loading';
import { WEBGATE_DOMAIN, webgateFetch } from '../../../shared/lib/api';
import { ArtistsResponse, Artist } from './types';
import { formatArtist } from './formatArtist';
import { useArtistGraphqlBool } from './useArtistGraphqlBool';
import { sdk } from './GetArtistByUris.gqlquery.generated';

const artistLoader = createBatchLoader(async (artistIds: string[]) => {
  const artistUris = artistIds.map(artistId => `spotify:artist:${artistId}`);

  const response = await webgateFetch(
    `${WEBGATE_DOMAIN}/creator-search-service/v0/artists/authorizeAndDecorate`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ artistUris }),
    },
  );

  const { artists } = (response.ok
    ? await response.json()
    : { artists: [] }) as ArtistsResponse;

  const artistMap = new Map(artists.map(artist => [artist.uri, artist]));

  // the backend doesn't guarantee order
  return artistUris.map(uri => {
    const artist = artistMap.get(uri);
    return artist ? formatArtist(artist) : null;
  });
});

const artistLoaderGraphql = createBatchLoader(async (artistIds: string[]) => {
  // eslint-disable-next-line new-cap
  const result = await sdk.GetArtistsByUris({ ids: artistIds });
  return result.artists;
});

/** For when useArtist can't be used because of conditional logic. */
export function getArtist(artistId: string) {
  return artistLoader.load(artistId);
}

export function useArtist(artistId: string) {
  return useRead(
    useArtistGraphqlBool() ? artistLoaderGraphql : artistLoader,
    artistId,
  );
}

/**
 * this function is for updating an artist in the cache if one exists, or for
 * priming a new artist. Currently used to keep the artist avatar up to date when
 * editing the artist profile
 * @param artist new artist object to update + store
 */
export async function updateArtist(artist: Artist) {
  artistLoader.clear(artist.id).prime(artist.id, {
    ...artist,
  });
}
